import get from 'lodash/get';
import omit from 'lodash/omit';
import camelize from 'camelize';
import { createReducer } from '../../utils/redux/index';
import consts from './constants';

const initState = {
  opened: false,
  ready: false,
  error: null,
  chatModuleInstance: null,
  activeChannelId: null,
  activeUnreadCount: 0,

  availableChannels: {}, // role / targetUser / channel
  joinedChannels: {}, // initialized channels instances

  uploads: {},
  downloads: {},
  lastActiveChannelIds: [],
};

const getChannelByTaskId = (channel, taskId) => {
  const list = Object.keys(channel).map(k => channel[k]);
  return list.find(item => item.inboxTaskId === taskId);
};

const parseChannel = channel => {
  const messages = get(channel, 'messages', []);

  return { ...channel, messages: camelize(messages) };
};

const updateJoinedChannels = (state, action) => {
  const channelId = get(action.payload, 'channelId');

  // handle add one channel
  if (channelId) {
    return {
      ...state,
      joinedChannels: Object.assign({}, state.joinedChannels, {
        [channelId]: parseChannel(action.payload),
      }),
    };
  }

  // handle add multiple channels
  const channels = action.payload;
  const newChannels = Object.keys(channels).reduce((acc, key) => {
    acc[key] = parseChannel(channels[key]);

    return acc;
  }, {});

  return {
    ...state,
    joinedChannels: Object.assign({}, state.joinedChannels, newChannels),
  };
};

const setAvailableChannels = (state, action) => {
  return {
    ...state,
    availableChannels: action.payload,
  };
};

const removeActiveUnreadCount = (state, action) => {
  return {
    ...state,
    activeUnreadCount: 0,
  };
};

const removeChat = (state, action) => {
  const {
    payload: { channelId, contextId },
  } = action;

  return {
    ...state,
    availableChannels: {
      ...state.availableChannels,
      [contextId]: omit(state.availableChannels[contextId], channelId),
    },
  };
};

const updateUploads = (state, action) => {
  const { channelId, id } = action.payload;

  const existedUpload = get(state.uploads, [channelId, id], {});
  const updatedUpload = Object.assign({}, existedUpload, action.payload);

  const updatedChannel = Object.assign({}, state.uploads[channelId], {
    [id]: updatedUpload,
  });

  return {
    ...state,
    uploads: Object.assign({}, state.uploads, {
      [channelId]: updatedChannel,
    }),
  };
};

const cleanUploads = (state, action) => {
  const { channelId, id } = action.payload;
  const newUploads = { ...state.uploads };

  delete newUploads[channelId][id];

  return {
    ...state,
    uploads: newUploads,
  };
};

const updateDownLoads = (state, action) => {
  const { channelId, id, parentMsgId } = action.payload;
  let updatedParent;

  if (parentMsgId) {
    const parentMsg = get(state.downloads, [channelId, parentMsgId]);
    const parentMsgReplies = get(parentMsg, 'replies');

    updatedParent = Object.assign({}, parentMsg, {
      replies: {
        ...parentMsgReplies,
        [id]: action.payload,
      },
    });
  }

  const updatedDownload = Object.assign({}, state.downloads[channelId], {
    [parentMsgId || id]: updatedParent || action.payload,
  });

  return {
    ...state,
    downloads: Object.assign({}, state.downloads, {
      [channelId]: updatedDownload,
    }),
  };
};

const cleanDownloads = (state, action) => {
  const { channelId, id, parentMsgId } = action.payload;
  const newDownloads = { ...state.downloads };

  if (parentMsgId) {
    const download = get(newDownloads, [channelId, parentMsgId, 'replies', id]);

    if (download) {
      delete newDownloads[channelId][parentMsgId].replies[id];
    }
  } else {
    delete newDownloads[channelId][id];
  }

  return {
    ...state,
    downloads: newDownloads,
  };
};

const addActiveUnreadCount = (state, action) => {
  return {
    ...state,
    activeUnreadCount: action.payload,
  };
};

const updateChannelInboxTask = (state, { payload }) => {
  const { id: inboxTaskId, patient } = { ...payload };
  const { id: patientId } = patient;

  const { joinedChannels, availableChannels } = state;

  const availablePatientChannels = get(availableChannels, [patientId], {});

  const availableChannel = getChannelByTaskId(
    availablePatientChannels,
    inboxTaskId,
  );
  const joinedChannel = getChannelByTaskId(joinedChannels, inboxTaskId);

  const channel = joinedChannel || availableChannel;

  if (channel) {
    const { channelId } = channel;

    const modifiedAvailableChannels = {
      ...availableChannels,
      [patientId]: {
        ...availableChannels[patientId],
        [channelId]: {
          ...availableChannels[patientId][channelId],
          inboxTaskId: null,
        },
      },
    };

    const modifiedJoinedChannels = {
      ...joinedChannels,
      [channelId]: {
        ...joinedChannels[channelId],
        inboxTaskId: null,
      },
    };

    if (joinedChannel) {
      return {
        ...state,
        joinedChannels: modifiedJoinedChannels,
        availableChannels: modifiedAvailableChannels,
      };
    }
    return {
      ...state,
      availableChannels: modifiedAvailableChannels,
    };
  }

  return {
    ...state,
  };
};

export default createReducer(initState, {
  [consts.CONNECTION_OPENED]: (state, action) => {
    return {
      ...state,
      opened: true,
      error: null,
    };
  },

  [consts.SET_CONNECT_ERROR]: (state, action) => {
    return {
      ...state,
      opened: false,
      error: action.payload,
    };
  },

  [consts.SET_CHAT_MODULE_INSTANCE]: (state, action) => {
    return {
      ...state,
      chatModuleInstance: action.payload,
    };
  },

  [consts.CONNECTION_READY]: (state, action) => {
    return {
      ...state,
      ready: true,
    };
  },

  [consts.SET_AVAILABLE_CHANNELS]: setAvailableChannels,
  [consts.UPDATE_AVAILABLE_CHANNELS]: setAvailableChannels,
  [consts.REMOVE_CONTEXT_CHANNEL]: removeChat,
  [consts.SET_ACTIVE_CHANNEL_ID]: (state, action) => {
    return {
      ...state,
      activeChannelId: action.payload,
    };
  },

  [consts.SET_CHANNEL_DATA]: updateJoinedChannels,

  [consts.UPDATE_ATTACHMENT_UPLOAD_STATUS]: updateUploads,
  [consts.REMOVE_UPLOAD_ATTACHMENT]: cleanUploads,

  [consts.UPDATE_ATTACHMENT_DOWNLOAD_STATUS]: updateDownLoads,
  [consts.REMOVE_DOWNLOAD_ATTACHMENT]: cleanDownloads,

  [consts.ADD_NEW_SUBCHANNEL]: (state, action) => {
    const {
      payload: {
        subChannel,
        id,
        subChannel: { channelId },
      },
    } = action;

    return {
      ...state,
      availableChannels: {
        ...state.availableChannels,
        [id]: {
          ...state.availableChannels[id],
          [channelId]: {
            ...subChannel,
          },
        },
      },
    };
  },

  [consts.SET_LAST_CHANNEL_ID]: (state, action) => {
    const {
      payload: { targetUserId, id },
    } = action;
    const isAdded = state.lastActiveChannelIds.find(
      item => item.targetUserId === targetUserId,
    );

    const lastChannels = state.lastActiveChannelIds.map(item => {
      if (item.targetUserId === targetUserId) {
        return { id, targetUserId };
      }
      return item;
    });

    return {
      ...state,
      lastActiveChannelIds: isAdded
        ? lastChannels
        : [...state.lastActiveChannelIds, action.payload],
    };
  },

  [consts.ADD_ACTIVE_UNREAD]: addActiveUnreadCount,
  [consts.REMOVE_ACTIVE_UNREAD]: removeActiveUnreadCount,
  [consts.UPDATE_CONTEXT_CHAT_NAME]: (state, action) => {
    const {
      contextId,
      payload: { channelId, channelName },
    } = action;

    return {
      ...state,
      availableChannels: {
        ...state.availableChannels,
        [contextId]: {
          ...state.availableChannels[contextId],
          [channelId]: {
            ...state.availableChannels[contextId][channelId],
            channelName,
          },
        },
      },
    };
  },
  [consts.UPDATE_CONTEXT_CHANNEL]: (state, action) => {
    const {
      activeChannelId,
      payload: {
        subchannels,
        contextUser: { id },
      },
    } = action;
    const subChannel = subchannels.find(
      item => get(item, 'channelId') === activeChannelId,
    );

    return {
      ...state,
      availableChannels: {
        ...state.availableChannels,
        [id]: {
          ...state.availableChannels[id],
          [activeChannelId]: {
            ...subChannel,
          },
        },
      },
    };
  },
  [consts.UPDATE_CHANNEL_INBOX_TASK_ID]: updateChannelInboxTask,
});



// WEBPACK FOOTER //
// ./src/redux/chatConnection/reducer.js